'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';
import { Button, Col, Container, Form, Row, Toast } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useTranslation } from '@/hooks/useTranslation';
import { AlertsType } from '@/constants/Alerts';

type Props = { locale: string };

const CForm = ({
  setShowToast,
  locale,
}: {
  setShowToast: (state: { show: boolean; message: string }) => void;
  locale: string;
}) => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { t } = useTranslation(locale);

  const schema = yup
    .object({
      email: yup
        .string()
        .email()
        .matches(
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
          t(AlertsType.ISATKO003)
        )
        .required()
        .label('Email'),
      name: yup.string().min(2, t('ContactUs/e-01')).max(200).required().label('Name'),
      message: yup.string().min(10, t('ContactUs/e-02')).max(3000).required().label('Message'),
    })
    .required();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const [error, setError] = useState<any>();
  const [counter, setCounter] = useState(0);
  const [token, setToken] = useState<string>();

  const handleReCaptchaVerify = useCallback(async () => {
    try {
      if (!executeRecaptcha) {
        return;
      }

      const token = await executeRecaptcha();

      setToken(token);
    } catch (error) {
      return error;
    }
  }, [executeRecaptcha]);

  const onSubmit = async (values: any) => {
    handleReCaptchaVerify().then(() => {
      // TODO ==> Replace counter with cookie
      if (counter <= 0) {
        // TODO ==> Call internal api
        values.reCapcha = token;

        axios
          .post(`/service/api/contactus`, values)
          .then(() => {
            setShowToast({
              show: true,
              message: t(AlertsType.ISCUKO004),
            });

            setCounter(counter + 1);

            reset();

            setError(null);

            setToken(undefined);
          })
          .catch((error) => {
            if (error?.response?.data?.message) {
              setError({
                message: t(AlertsType.ISCUKO003),
              });
            }
          });
      } else {
        setError({
          message: t(AlertsType.ISCUKO002),
        });
      }
    });
  };

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <Form.Group className="mb-3">
        <Form.Label htmlFor="contact_name" className="login-label">
          {t('ContactUs/l-01')}
        </Form.Label>
        <Form.Control id="contact_name" type="text" isInvalid={!!errors.name || error?.name} {...register('name')} />
        <Form.Control.Feedback type="invalid">{errors.name?.message || error?.name}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="contact_email" className="login-label">
          {t('ContactUs/l-02')}
        </Form.Label>
        <Form.Control
          id="contact_email"
          type="email"
          isInvalid={!!errors.email || error?.email}
          {...register('email')}
        />
        <Form.Control.Feedback type="invalid">{errors.email?.message || error?.email}</Form.Control.Feedback>
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label htmlFor="contact_message" className="login-label">
          {t('ContactUs/l-03')}
        </Form.Label>
        <Form.Control
          id="contact_message"
          style={{ resize: 'none' }}
          isInvalid={!!errors.message || error?.message}
          as="textarea"
          rows={8}
          {...register('message')}
        />
        <Form.Control.Feedback type="invalid">{errors.message?.message || error?.message}</Form.Control.Feedback>
      </Form.Group>

      <Button
        variant="primary"
        type="submit"
        className="tw-w-full tw-py-3 tw-px-6 tw-rounded-xl tw-justify-center tw-text-center tw-font-semibold tw-font-['Lexend'] tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 tw-shadow-lg hover:tw-shadow-xl"
        // disabled={isCaptchaReady}
      >
        {t('ContactUs/l-04')}
      </Button>
    </Form>
  );
};

const ContactusComponent = ({ locale }: Props) => {
  const { t } = useTranslation(locale);

  const [toast, setToast] = useState<{ show: boolean; message: string }>({
    show: false,
    message: '',
  });

  return (
    <GoogleReCaptchaProvider useEnterprise={true} reCaptchaKey={process.env.NEXT_PUBLIC_GOOGLE_RECAPTCHA as string}>
      <div className="position-relative">
        <Container fluid="lg px-lg-5 ">
          <Row className="px-lg-5 mx-lg-5">
            <Col className="px-lg-5 mx-lg-5">
              <Row className="mt-5">
                <Col className="d-flex justify-content-center">
                  <h1 className="font-poppins fw-bold tw-text-5xl">{t('ContactUs/title')}</h1>
                </Col>
              </Row>
              <Row>
                <Col className="d-flex justify-content-center">
                  <h2 className="fs-5 fw-light">{t('ContactUs/text-01')}</h2>
                </Col>
              </Row>
              <Row className="px-lg-5 mx-lg-5 my-3">
                <Col>
                  <CForm setShowToast={setToast} locale={locale} />
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>

        <Toast
          onClose={() => setToast({ show: false, message: '' })}
          show={toast.show}
          delay={3000}
          autohide
          style={{ position: 'fixed', left: 10, bottom: 65 }}
        >
          <Toast.Header>
            <strong className="me-auto text-success">Success</strong>
          </Toast.Header>
          <Toast.Body>{toast.message}</Toast.Body>
        </Toast>
      </div>
    </GoogleReCaptchaProvider>
  );
};

export default ContactusComponent;
